"use client";

import { useEffect } from "react";

/**
 * Restores deferred rich-content media authored in operator HTML:
 *
 * - `<iframe data-embed-src>` (sanitizeHtml strips every iframe `src`): the
 *   SSR markup ships zero embed bytes — no YouTube player JS, no map tiles.
 *   YouTube embeds become a **click-to-load facade** (an `i.ytimg.com` poster
 *   thumbnail + play button; the ~785 KB player loads only on click). This is
 *   deliberately a facade rather than a viewport lazy-load: unsized iframes
 *   collapse to ~0 px before images load, so a viewport observer would see
 *   every embed "near the viewport" at once and pull *all* of them (measured:
 *   12 videos → 2.2 MB / ~220 requests, LCP 9.3 s). Non-YouTube embeds (maps,
 *   Vimeo, …) get their `src` back ~200 px before they scroll into view.
 *
 * - `<img data-defer-src>` (optimizeBodyImages defers body photos): native
 *   `loading="lazy"` can't stop a photo 1–2 viewports down from downloading
 *   inside the LCP window (Chrome's threshold is 1250–2500 px; measured
 *   ~390 KB pre-LCP). A 300 px IntersectionObserver restores src/srcset
 *   just-in-time; the reserved `aspect-ratio` box means zero layout shift.
 *
 * Discovery is a MutationObserver, NOT a per-navigation scan: detail-page
 * sections stream in under `<Suspense>` and can reach the DOM after any
 * one-shot effect has already run — a scan-once design would leave their
 * images unrestored forever (and soft navigations, React re-renders, and
 * the Google Translate DOM rewrite all move content around at arbitrary
 * times). Watching `childList` mutations makes restoration unconditional
 * on timing. Attribute-only changes are not observed, and every restore
 * removes the marker attribute, so processing is idempotent and loop-free.
 *
 * Mounted once in the root layout; near-zero cost on routes with no media.
 */

const YT_HOSTS = ["youtube.com", "youtube-nocookie.com"];
const PLAY_REST =
  "color-mix(in srgb, var(--tc--preset--color--heading) 62%, transparent)";

function youtubeId(src: string): string | null {
  try {
    const u = new URL(src, "https://placeholder.invalid");
    if (!YT_HOSTS.some((h) => u.hostname === h || u.hostname.endsWith(`.${h}`)))
      return null;
    const m = u.pathname.match(/\/embed\/([A-Za-z0-9_-]{6,})/);
    return m?.[1] ?? null;
  } catch {
    return null;
  }
}

/**
 * Paint the sharpest available YouTube thumbnail onto the facade poster.
 *
 * Prefer the WebP maxres thumbnail (1280×720, native 16:9): hqdefault is only
 * 480×360 and 4:3, so cover-cropped into the full-width 16:9 facade box it
 * upscales ~3× on a retina column and visibly pixelates. maxres 404s for videos
 * the uploader never gave an HD thumbnail, so probe it and fall back to the
 * always-present hqdefault JPG (a 200-OK 120×90 grey placeholder also fails the
 * width guard). WebP keeps the crisp rung ~30–45% lighter than the JPG maxres —
 * it matters because the poster is below-fold and viewport-lazy, so it competes
 * for bandwidth on scroll. The probe's fetch is reused as the background — same
 * URL, so a cache hit rather than a second download.
 */
function paintFacadeThumbnail(poster: HTMLElement, id: string): void {
  const hq = `url("https://i.ytimg.com/vi/${id}/hqdefault.jpg")`;
  const max = `https://i.ytimg.com/vi_webp/${id}/maxresdefault.webp`;
  const probe = new Image();
  probe.onload = () => {
    poster.style.backgroundImage =
      probe.naturalWidth > 120 ? `url("${max}")` : hq;
  };
  probe.onerror = () => {
    poster.style.backgroundImage = hq;
  };
  probe.src = max;
}

/** Swap a deferred YouTube iframe for a clickable poster; load the player on click. */
function facadeYouTube(
  frame: HTMLIFrameElement,
  src: string,
  id: string,
): void {
  const poster = document.createElement("button");
  poster.type = "button";
  poster.setAttribute("aria-label", "Play video");
  poster.style.cssText = [
    "position:relative",
    "display:block",
    "width:100%",
    "border:0",
    "padding:0",
    "margin:0",
    "cursor:pointer",
    "border-radius:0.75rem",
    "overflow:hidden",
    "background-color:var(--tc--preset--color--heading)",
    "background-position:center",
    "background-size:cover",
    "background-repeat:no-repeat",
  ].join(";");
  // Occupy EXACTLY the box the sanitized iframe reserved (sanitize.ts locks
  // `width:100%;aspect-ratio:<declared W/H>` on every embed). Hardcoding
  // 16/9 here would re-introduce a layout shift for any non-16:9 embed the
  // moment the facade swaps in.
  poster.style.aspectRatio = frame.style.aspectRatio || "16 / 9";
  // The thumbnail itself is viewport-lazy: the facade has intrinsic size
  // (aspect-ratio above), so — unlike the collapsed iframes that motivated
  // the click-facade — an IntersectionObserver works correctly here. Videos
  // below the fold cost zero bytes until scrolled near, so the poster never
  // competes with the LCP image for bandwidth (see paintFacadeThumbnail).
  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            paintFacadeThumbnail(poster, id);
            obs.disconnect();
          }
        }
      },
      { rootMargin: "400px" },
    );
    io.observe(poster);
  } else {
    paintFacadeThumbnail(poster, id);
  }

  const play = document.createElement("span");
  play.setAttribute("aria-hidden", "true");
  play.style.cssText = [
    "position:absolute",
    "left:50%",
    "top:50%",
    "transform:translate(-50%,-50%)",
    "width:68px",
    "height:48px",
    "border-radius:14px",
    `background:${PLAY_REST}`,
    "display:flex",
    "align-items:center",
    "justify-content:center",
    "transition:background 0.15s ease",
  ].join(";");
  play.innerHTML =
    '<svg width="26" height="26" viewBox="0 0 24 24" style="fill:var(--tc--preset--color--background)" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
  poster.appendChild(play);
  poster.addEventListener("mouseenter", () => {
    play.style.background =
      "color-mix(in srgb, var(--tc--preset--color--destructive) 92%, transparent)";
  });
  poster.addEventListener("mouseleave", () => {
    play.style.background = PLAY_REST;
  });

  poster.addEventListener(
    "click",
    () => {
      // Autoplay on the facade click — the poster IS the play affordance, so one
      // gesture should start the video. Without it the swapped-in iframe loads
      // YouTube's *paused* player (its own poster + chrome, which reads smaller
      // than the full-bleed facade), forcing a second click on YouTube's play
      // button. Matches the reviews gallery (videoEmbedUrl appends autoplay=1);
      // autoplay is honoured only when the operator's embed carries
      // allow="autoplay" (the sanitiser keeps it but never adds it) — otherwise
      // it's a harmless no-op. Resolve against the page origin so a
      // protocol-relative embed (`//www.youtube.com/embed/…`, which youtubeId()
      // still accepts via its base fallback) doesn't throw here.
      const playSrc = new URL(src, window.location.href);
      playSrc.searchParams.set("autoplay", "1");
      frame.setAttribute("src", playSrc.toString());
      frame.removeAttribute("data-embed-src");
      frame.style.display = "";
      poster.replaceWith(frame);
    },
    { once: true },
  );

  frame.style.display = "none";
  frame.replaceWith(poster);
}

const IMG_SELECTOR = "img[data-defer-src]";
const FRAME_SELECTOR = "iframe[data-embed-src]";

export function LazyEmbeds(): null {
  useEffect(() => {
    const hasIO = "IntersectionObserver" in window;
    // Idempotency guard: the initial scan and the MutationObserver can both
    // deliver the same node (insertion racing the scan), and a subtree move
    // re-fires childList for nodes already wired.
    const seen = new WeakSet<Element>();

    const restoreImg = (el: HTMLImageElement): void => {
      const srcset = el.getAttribute("data-defer-srcset");
      const src = el.getAttribute("data-defer-src");
      // srcset first, so the browser never fetches the fallback src rung.
      if (srcset) el.setAttribute("srcset", srcset);
      if (src) el.setAttribute("src", src);
      el.removeAttribute("data-defer-srcset");
      el.removeAttribute("data-defer-src");
    };
    const loadFrame = (el: HTMLIFrameElement): void => {
      const src = el.getAttribute("data-embed-src");
      if (!src) return;
      el.setAttribute("src", src);
      el.removeAttribute("data-embed-src");
    };

    const imgIO = hasIO
      ? new IntersectionObserver(
          (entries, obs) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                restoreImg(entry.target as HTMLImageElement);
                obs.unobserve(entry.target);
              }
            }
          },
          { rootMargin: "300px" },
        )
      : null;
    const frameIO = hasIO
      ? new IntersectionObserver(
          (entries, obs) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                loadFrame(entry.target as HTMLIFrameElement);
                obs.unobserve(entry.target);
              }
            }
          },
          { rootMargin: "200px" },
        )
      : null;

    const processImg = (el: HTMLImageElement): void => {
      if (seen.has(el)) return;
      seen.add(el);
      if (imgIO) imgIO.observe(el);
      else restoreImg(el);
    };
    const processFrame = (el: HTMLIFrameElement): void => {
      if (seen.has(el)) return;
      seen.add(el);
      const src = el.getAttribute("data-embed-src");
      if (!src) return;
      const id = youtubeId(src);
      if (id) facadeYouTube(el, src, id);
      else if (frameIO) frameIO.observe(el);
      else loadFrame(el);
    };
    const scan = (root: Element | Document): void => {
      root.querySelectorAll<HTMLImageElement>(IMG_SELECTOR).forEach(processImg);
      root
        .querySelectorAll<HTMLIFrameElement>(FRAME_SELECTOR)
        .forEach(processFrame);
    };

    scan(document);

    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          const el = node as Element;
          if (el.matches(IMG_SELECTOR)) processImg(el as HTMLImageElement);
          else if (el.matches(FRAME_SELECTOR))
            processFrame(el as HTMLIFrameElement);
          scan(el);
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      imgIO?.disconnect();
      frameIO?.disconnect();
    };
  }, []);

  return null;
}
